import { useState, useCallback } from "react";
import { useDataProvider, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Phone, User, ArrowRight, Loader2 } from "lucide-react";

interface OnboardingData {
  orgName: string;
  twilioNumber: string;
  adminName: string;
}

/**
 * BldrOS first-run onboarding flow.
 *
 * Shows when the current user has no organization.
 * Creates the org, assigns the user as admin, and refreshes.
 */
export function BldrOsOnboarding() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<OnboardingData>({
    orgName: "",
    twilioNumber: "",
    adminName: "",
  });
  const [saving, setSaving] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSubmit = useCallback(async () => {
    if (!data.orgName.trim()) {
      notify("Please enter your company name", { type: "warning" });
      return;
    }

    setSaving(true);
    try {
      await dataProvider.create("organizations", {
        data: {
          name: data.orgName.trim(),
          twilio_phone_number: data.twilioNumber.trim() || null,
          settings: {
            admin_name: data.adminName.trim() || null,
            onboarding_completed: true,
            onboarding_completed_at: new Date().toISOString(),
          },
        },
      });

      notify("Organization created! Welcome to BldrOS.", { type: "success" });
      refresh();
    } catch (err) {
      console.error("Onboarding failed:", err);
      notify(`Setup failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  }, [data, dataProvider, notify, refresh]);

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="w-full max-w-lg">
        <CardContent className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 mb-4">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold">Welcome to BldrOS</h2>
            <p className="text-muted-foreground mt-2">
              Let&apos;s set up your construction business in 60 seconds.
            </p>
          </div>

          {/* Progress */}
          <div className="flex gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className="flex-1 h-1.5 rounded-full transition-colors"
                style={{
                  backgroundColor: s <= step ? "#2563eb" : "#e5e7eb",
                }}
              />
            ))}
          </div>

          {/* Step 1: Company name */}
          {step === 1 && (
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4" />
                  Company name
                </span>
                <input
                  type="text"
                  value={data.orgName}
                  onChange={(e) =>
                    setData((d) => ({ ...d, orgName: e.target.value }))
                  }
                  placeholder="e.g. Summit Builders LLC"
                  autoFocus
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </label>
              <button
                onClick={() => {
                  if (data.orgName.trim()) setStep(2);
                }}
                disabled={!data.orgName.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-colors"
                style={{
                  backgroundColor: data.orgName.trim() ? "#2563eb" : "#93c5fd",
                  cursor: data.orgName.trim() ? "pointer" : "not-allowed",
                }}
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step 2: Twilio number */}
          {step === 2 && (
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4" />
                  Twilio phone number (optional)
                </span>
                <input
                  type="tel"
                  value={data.twilioNumber}
                  onChange={(e) =>
                    setData((d) => ({ ...d, twilioNumber: e.target.value }))
                  }
                  placeholder="+15551234567"
                  autoFocus
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-xs text-muted-foreground mt-1 block">
                  You can configure this later in Settings.
                </span>
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm & create */}
          {step === 3 && (
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  Your name (optional)
                </span>
                <input
                  type="text"
                  value={data.adminName}
                  onChange={(e) =>
                    setData((d) => ({ ...d, adminName: e.target.value }))
                  }
                  placeholder="e.g. Barrett Sharpe"
                  autoFocus
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </label>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Company</span>
                  <span className="font-medium">{data.orgName}</span>
                </div>
                {data.twilioNumber && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Phone</span>
                    <span className="font-medium">{data.twilioNumber}</span>
                  </div>
                )}
                {data.adminName && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Admin</span>
                    <span className="font-medium">{data.adminName}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setStep(2)}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Launch BldrOS"
                  )}
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
